import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CrisisBar } from "@/components/CrisisBar";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { t } from "@/lib/i18n";

export const STAFF_SECTIONS = [
  { to: "/staff", key: "staff.dashboard", end: true, roles: null },
  { to: "/staff/urgent", key: "staff.urgent", roles: ["care_director", "care_coordinator", "supervisor", "counselor"] },
  { to: "/staff/intake", key: "staff.intake", roles: ["care_director", "care_coordinator"] },
  { to: "/staff/cases", key: "staff.cases", roles: ["care_director", "care_coordinator", "supervisor", "counselor", "system_admin"] },
  { to: "/staff/calendar", key: "staff.calendar", roles: ["care_director", "care_coordinator", "supervisor", "counselor"] },
  { to: "/staff/messages", key: "staff.messages", roles: ["care_director", "care_coordinator", "supervisor", "counselor"] },
  { to: "/staff/videos", key: "staff.videos", roles: ["care_director", "content_editor", "care_coordinator", "supervisor", "counselor"] },
  { to: "/staff/providers", key: "staff.providers", roles: ["care_director", "care_coordinator", "supervisor"] },
  { to: "/staff/classes", key: "staff.classes", phase: 7 },
  { to: "/staff/resources", key: "staff.resources", roles: ["content_editor", "care_director", "care_coordinator", "supervisor", "counselor"] },
  { to: "/staff/reach-out", key: "staff.reachOut", roles: ["care_director"] },
  { to: "/staff/communications", key: "staff.communications", roles: ["system_admin", "care_director", "care_coordinator"] },
  { to: "/staff/forms", key: "staff.forms", roles: ["care_director", "care_coordinator", "system_admin"] },
  { to: "/staff/people", key: "staff.people", roles: ["system_admin", "care_director"] },
  { to: "/staff/reports", key: "staff.reports", phase: 8 },
  { to: "/staff/records", key: "staff.records", phase: 8 },
  { to: "/staff/audit", key: "staff.audit", roles: ["system_admin", "care_director"] },
  { to: "/staff/settings", key: "staff.settings", roles: ["system_admin", "care_director"] },
  { to: "/staff/content", key: "staff.content", roles: ["content_editor", "care_director"] },
  { to: "/staff/import", key: "staff.import", phase: 8 },
  { to: "/staff/demo", key: "staff.demo", phase: 8 },
];

export const StaffLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const signOut = async () => { await logout(); navigate("/"); };
  const canSee = (s) => !s.roles || s.roles.some((r) => user.roles.includes(r));

  const nav = (
    <nav aria-label="Staff console" className="flex flex-col gap-0.5">
      {STAFF_SECTIONS.map((s) => {
        const slug = s.to.split("/").pop() || "dashboard";
        if (s.phase) {
          return (
            <NavLink key={s.to} to={s.to} onClick={() => setOpen(false)} className={({ isActive }) => `tap justify-between rounded px-3 text-left text-ink-500 hover:bg-cream-50 ${isActive ? "bg-cream-50" : ""}`} data-testid={`staff-nav-${slug}`}>
              <span>{t(s.key)}</span><span className="text-xs">{t("staff.phase", { n: s.phase })}</span>
            </NavLink>
          );
        }
        if (!canSee(s)) return null;
        return (
          <NavLink key={s.to} to={s.to} end={s.end} onClick={() => setOpen(false)} className={({ isActive }) => `tap justify-start rounded px-3 text-left font-semibold hover:bg-cream-50 ${isActive ? "bg-olive-900 text-cream-50 hover:bg-olive-900" : "text-ink-900"}`} data-testid={`staff-nav-${slug}`}>
            {t(s.key)}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col bg-cream-50 text-ink-900">
      <a href="#main" className="sr-only-focusable fixed left-4 top-4 z-50 rounded bg-olive-900 px-4 py-3 text-cream-50">{t("nav.skip")}</a>
      <CrisisBar />
      <header className="bg-olive-900 text-cream-50" data-testid="staff-header">
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button type="button" className="tap lg:hidden rounded hover:bg-cream-50/10" aria-expanded={open} aria-controls="staff-sidebar" onClick={() => setOpen((v) => !v)} data-testid="staff-menu-button">
              {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}<span className="sr-only">{t("nav.menu")}</span>
            </button>
            <Wordmark to="/staff" sub={false} />
            <span className="hidden sm:inline text-sm opacity-90">{t("staff.console")}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden md:inline" data-testid="staff-signed-in-as">{t("staff.signedInAs")} {user.first_name} {user.last_name}</span>
            <NavLink to="/account/security" className="tap px-2 rounded hover:bg-cream-50/10" data-testid="staff-security-link">{user.mfa_enabled ? "MFA On" : "MFA Off"}</NavLink>
            <button type="button" onClick={signOut} className="tap px-3 rounded border border-cream-50/60 hover:bg-cream-50/10" data-testid="staff-sign-out">{t("nav.signOut")}</button>
          </div>
        </div>
      </header>
      <div className="flex flex-1">
        <aside id="staff-sidebar" className={`${open ? "block" : "hidden"} lg:block w-full lg:w-72 shrink-0 border-r border-sage-400/60 bg-cream-100 p-4 lg:p-5`} data-testid="staff-sidebar">
          {nav}
        </aside>
        <main id="main" className={`${open ? "hidden lg:block" : "block"} flex-1 min-w-0 p-5 sm:p-8 lg:p-10`} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};
